// Retry helper utility with exponential backoff
// Handles retry logic for transient network failures

/**
 * Checks if an error is retryable (transient error)
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if error is retryable
 */
export function isRetryableError(error) {
    if (!error) return false;
    
    // Network errors are retryable
    if (error.message && (
        error.message.includes('timeout') ||
        error.message.includes('network') ||
        error.message.includes('Network Error') ||
        error.message.includes('Failed to fetch')
    )) {
        return true;
    }
    
    // Error objects with retryable flag
    if (error.retryable === false) {
        return false;
    }
    
    if (error.retryable === true) {
        return true;
    }
    
    // HTTP status codes that are retryable
    if (error.response) {
        const status = error.response.status;
        // 408 Request Timeout, 429 Too Many Requests, 500-599 Server Errors
        if (status === 408 || status === 429 || (status >= 500 && status < 600)) {
            return true;
        }
    }
    
    // Error codes that are retryable
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.code === 'ENOTFOUND') {
        return true;
    }
    
    return false;
}

/**
 * Calculates delay for exponential backoff
 * @param {number} attemptNumber - Current attempt number (0-indexed)
 * @param {number} baseDelayMs - Base delay in milliseconds (default: 1000ms)
 * @param {number} maxDelayMs - Maximum delay in milliseconds (default: 10000ms)
 * @returns {number} Delay in milliseconds
 */
export function calculateBackoffDelay(attemptNumber, baseDelayMs = 1000, maxDelayMs = 10000) {
    // Exponential backoff: baseDelay * 2^attemptNumber
    var delay = baseDelayMs * Math.pow(2, attemptNumber);
    // Cap at max delay
    return Math.min(delay, maxDelayMs);
}

/**
 * Waits for specified milliseconds
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Promise that resolves after delay
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retries a function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.baseDelayMs - Base delay for backoff (default: 1000ms)
 * @param {number} options.maxDelayMs - Maximum delay (default: 10000ms)
 * @param {Function} options.shouldRetry - Custom function to determine if error is retryable
 * @param {Function} options.onRetry - Callback called before each retry
 * @returns {Promise} Result of the function or throws last error
 */
export async function retryWithBackoff(fn, options = {}) {
    const {
        maxRetries = 3,
        baseDelayMs = 1000,
        maxDelayMs = 10000,
        shouldRetry = isRetryableError,
        onRetry = null
    } = options;
    
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            
            // Check if we should retry
            if (attempt < maxRetries && shouldRetry(error)) {
                const delayMs = calculateBackoffDelay(attempt, baseDelayMs, maxDelayMs);
                
                if (onRetry) {
                    onRetry(attempt + 1, maxRetries, delayMs, error);
                } else {
                    console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms. Error:`, error.message || error);
                }
                
                // Wait before retrying
                await delay(delayMs);
                continue;
            }
            
            // Not retryable or max retries reached
            break;
        }
    }
    
    // All retries exhausted, throw last error
    throw lastError;
}

